/**
 * RET-2452 (KTD-3, KTD-5, KTD-6): the past-due payment zone's view, built from
 * what the summary, the card list and the zone's own local state already hold.
 * Pure: no React, no services, no clock except where a caller passes `now`.
 * The subscription manager hook keeps the local state and wires the callbacks.
 */
import { PAST_DUE_FINAL_DAY } from 'src/alerts/AlertCenter.constants';
import { VITE_PAST_DUE_LADDER_FEATURE } from 'src/constants/envs';
import type {
  tSubscriptionSummary,
  tSummaryDebtInvoice,
  tSummaryDebtInvoiceLine,
  tSummaryLastDecline,
} from 'src/providers/SubscriptionProvider/Subscription.types';
import { formatAmountOwed } from 'src/utils/pastDueDisclosure';
import {
  isExpiredCard,
  PAST_DUE_HOSTED_OPTION,
  resolveInitialPastDueSelection,
  type tPastDueDebtInvoice,
  type tPastDueDebtLine,
  type tPastDueDecline,
  type tPastDueDeclineGroup,
  type tPastDuePaymentPhase,
  type tPastDuePaymentView,
} from './components/SubscriptionOverview/PastDuePaymentZone.types';
import { isSubscriptionPastDue } from './SubscriptionManager.functions';
import type {
  tPaymentMethod,
  tPaymentMethodDefaults,
  tPreviousPaymentMethods,
  tRedirectReturnSnapshot,
} from './SubscriptionManager.types';

/** Whether the summary of the current workspace arrived, is on its way, or failed. */
export type tPastDueSummaryState = 'loading' | 'loaded' | 'failed';

/** The phases the zone's own state can hold. The rest come from the summary. */
export type tPastDueLocalPhase = Extract<
  tPastDuePaymentPhase,
  'idle' | 'confirming' | 'partial' | 'debtChanged' | 'hostedOpened' | 'settling'
>;

/**
 * What the zone keeps for itself between renders. `selectedPaymentMethodId` is
 * the customer's own pick (or the card being paid, or the card just added);
 * `null` lets the view choose with `resolveInitialPastDueSelection`. It is not
 * the hook's `selectedPaymentMethod`, which the checkout and the top-up use.
 */
export type tPastDueLocalState = {
  phase: tPastDueLocalPhase;
  selectedPaymentMethodId: string | null;
  /** The decline of a payment made on this screen. Wins over the renewal's (KTD-5). */
  decline: tPastDueDecline | null;
  partial: { paid: string; total: string; remaining: string } | null;
};

export const PAST_DUE_LOCAL_INITIAL_STATE: tPastDueLocalState = {
  phase: 'idle',
  selectedPaymentMethodId: null,
  decline: null,
  partial: null,
};

export type tBuildPastDuePaymentViewOptions = {
  summary: tSubscriptionSummary;
  summaryState: tPastDueSummaryState;
  /** `useSubscriptionContext().daysPastDue`: resolved days, `null` when not past due. */
  daysPastDue: number | null;
  paymentMethods: tPaymentMethod[];
  paymentMethodDefaults: tPaymentMethodDefaults;
  local: tPastDueLocalState;
  callbacks: Pick<
    tPastDuePaymentView,
    'onSelectPaymentMethod' | 'onAddPaymentMethod' | 'onPay'
  >;
};

/**
 * The one answer to «is the past-due payment zone on»: the zone's stored flag
 * (`billing_past-due-payment-zone`), the ladder's build flag
 * (`VITE_PAST_DUE_LADDER_FEATURE`), a past-due plan and a role that can pay
 * (Owner or Admin, RO-1). The hook and the overview both read it, so a toast
 * never speaks of a zone the screen does not draw.
 */
export function isPastDueZoneOn(options: {
  zoneEnabled: boolean;
  isPastDue: boolean;
  canPay: boolean;
}): boolean {
  return (
    options.zoneEnabled &&
    VITE_PAST_DUE_LADDER_FEATURE &&
    options.isPastDue &&
    options.canPay
  );
}

/** The decline groups billing classifies (M-21). Anything else reads as `generic`. */
const BILLING_DECLINE_GROUPS: readonly tPastDueDeclineGroup[] = [
  'insufficient_funds',
  'processing_error',
  'temporary_bank_error',
  'expired_card',
  'incorrect_cvc',
  'security_block',
  'limit_reached',
  'brand_not_supported',
  'generic',
];

/**
 * leadbilling's `DebtInvoiceLineKind` (snake_case, an open set) → the
 * prototype's closed list. What is not known reads «Otro cargo» (KTD-6).
 */
const DEBT_LINE_KINDS: Record<string, tPastDueDebtLine['kind']> = {
  plan: 'plan',
  seats_included: 'seatsIncluded',
  seats_extra: 'seatsExtra',
  add_on: 'addOn',
  pause_fee: 'pauseFee',
  proration: 'proration',
  discount: 'discount',
  credit: 'credit',
  tax: 'tax',
  wallet_top_up: 'walletTopUp',
  other: 'other',
};

const MINOR_UNITS_PER_UNIT = 100;

/**
 * A signed amount in the format of `formatAmountOwed`, for the breakdown:
 * negative lines (discount, credit) carry «−» and `0` is a real «$0.00» (the
 * seats the plan includes). `formatAmountOwed` returns `null` for both, which
 * is right for «Lo que debes» and wrong for a line. Same shape as the
 * prototype's harness formatter.
 */
export function formatDebtAmount(options: {
  amountMinorUnits: number | null | undefined;
  currency: string | null | undefined;
}): string | null {
  const { amountMinorUnits, currency } = options;
  if (
    amountMinorUnits === null ||
    amountMinorUnits === undefined ||
    Number.isNaN(amountMinorUnits)
  ) {
    return null;
  }
  const amount = (
    Math.abs(amountMinorUnits) / MINOR_UNITS_PER_UNIT
  ).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = amountMinorUnits < 0 ? '−' : '';
  return currency ? `${sign}$${amount} ${currency}` : `${sign}$${amount}`;
}

/** ISO 8601 → `Date`; `null` for an absent or unreadable value. */
export function parseSummaryDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function displayCurrency(summary: tSubscriptionSummary): string | null {
  return summary.currency ? summary.currency : null;
}

function toDebtLine(
  line: tSummaryDebtInvoiceLine,
  currency: string | null,
): tPastDueDebtLine {
  const kind =
    (line.kind !== null && DEBT_LINE_KINDS[line.kind]) || 'other';
  return {
    kind,
    ...(line.name ? { name: line.name } : {}),
    quantity: line.quantity,
    unitAmount: formatDebtAmount({ amountMinorUnits: line.unitAmount, currency }),
    amount: formatDebtAmount({ amountMinorUnits: line.amount, currency }) ?? '',
    periodStart: parseSummaryDate(line.periodStart),
    periodEnd: parseSummaryDate(line.periodEnd),
  };
}

/**
 * KTD-6: the summary's `debtInvoices` → the prototype's invoices. A field that
 * came `null` is painted empty, never a reason to drop the invoice: the
 * breakdown must add up to what the customer is asked to pay.
 */
export function mapDebtInvoices(options: {
  debtInvoices: tSummaryDebtInvoice[] | null | undefined;
  currency: string | null;
}): tPastDueDebtInvoice[] {
  const { debtInvoices, currency } = options;
  if (!debtInvoices) return [];
  return debtInvoices.map((invoice) => {
    const invoiceCurrency =
      currency ?? (invoice.currency ? invoice.currency.toUpperCase() : null);
    const lines = (invoice.lines ?? []).map((line) =>
      toDebtLine(line, invoiceCurrency),
    );
    const format = (amountMinorUnits: number | null) =>
      formatDebtAmount({ amountMinorUnits, currency: invoiceCurrency }) ?? '';
    return {
      invoiceId: invoice.invoiceId ?? '',
      number: invoice.number ?? '',
      // `issuedAt` is a `Date` in the prototype's type. Without one, the first
      // line's period start is the closest honest date there is.
      issuedAt:
        parseSummaryDate(invoice.issuedAt) ??
        lines.find((line) => line.periodStart !== null)?.periodStart ??
        new Date(Number.NaN),
      lines,
      total: format(invoice.total),
      amountPaid: format(invoice.amountPaid),
      amountRemaining: format(invoice.amountRemaining),
      invoicePdfUrl: invoice.invoicePdf ?? null,
    };
  });
}

/** A billing `text_group` (open set) → the zone's group. Unknown or absent → `generic`. */
export function toPastDueDeclineGroup(
  textGroup: string | null | undefined,
): tPastDueDeclineGroup {
  const known = BILLING_DECLINE_GROUPS.find((group) => group === textGroup);
  return known ?? 'generic';
}

/** `daily` | `monthly`, anything else `null`. */
export function toRetryLimit(
  value: string | null | undefined,
): tPastDueDecline['retryLimit'] {
  return value === 'daily' || value === 'monthly' ? value : null;
}

/**
 * KTD-5: the renewal's decline, read from the summary when the screen opens.
 * Only with `lastDeclineSource = 'stripe'`: `none` and `unavailable` carry no
 * notice.
 */
export function mapRenewalDecline(options: {
  lastDecline: tSummaryLastDecline | null | undefined;
  lastDeclineSource: string | null | undefined;
  currency: string | null | undefined;
}): tPastDueDecline | null {
  const { lastDecline, lastDeclineSource, currency } = options;
  if (lastDeclineSource !== 'stripe' || !lastDecline) return null;
  return {
    group: toPastDueDeclineGroup(lastDecline.textGroup),
    retryAllowed: lastDecline.retryAllowed === true,
    paymentMethodId: lastDecline.paymentMethodId,
    cardBrand: lastDecline.cardBrand,
    cardLast4: lastDecline.cardLast4,
    currency: currency ? currency.toLowerCase() : null,
    declinedAt: parseSummaryDate(lastDecline.declinedAt),
    cardsRestored: 'not_applicable',
    origin: 'renewal',
    retryLimit: toRetryLimit(lastDecline.retryLimit),
  };
}

/**
 * The ids of the invoices that make up `amountRemaining`, as the POST must
 * send them (`expected_invoice_ids`). `null` when the summary did not answer
 * them: the list is absent or empty, or an invoice has no id. leadbilling
 * compares the id set, so a POST without them answers `debt_changed` forever.
 */
export function readDebtInvoiceIds(
  summary: Pick<tSubscriptionSummary, 'debtInvoices'>,
): string[] | null {
  const invoices = summary.debtInvoices;
  if (!Array.isArray(invoices) || invoices.length === 0) return null;
  const ids = invoices.map((invoice) => invoice.invoiceId);
  return ids.every((id): id is string => typeof id === 'string' && id !== '')
    ? ids
    : null;
}

/**
 * The phase the summary alone gives, before the zone's own state. `settling`
 * is the backend's signal without a state of its own: past due, and Stripe
 * has no open invoice (`openInvoiceSource = none`).
 */
export function resolveSummaryPhase(options: {
  summary: tSubscriptionSummary;
  summaryState: tPastDueSummaryState;
}): tPastDuePaymentPhase {
  const { summary, summaryState } = options;
  if (summaryState === 'loading') return 'loading';
  if (summaryState === 'failed') return 'amountUnavailable';
  if (summary.openInvoiceSource === 'unavailable') return 'amountUnavailable';
  if (
    isSubscriptionPastDue(summary.status) &&
    summary.openInvoiceSource === 'none'
  ) {
    return 'settling';
  }
  const amountOwed = formatAmountOwed({
    amountMinorUnits: summary.amountRemaining,
    currency: summary.currency,
  });
  // An amount without the ids of its invoices cannot be paid: same notice.
  return amountOwed === null || readDebtInvoiceIds(summary) === null
    ? 'amountUnavailable'
    : 'idle';
}

/**
 * The zone's selection: the customer's own pick while it can still be paid
 * with, else the prototype's rule (the plan's card unless it is the refused
 * one or expired; else another usable card).
 */
export function resolvePastDueSelection(options: {
  chosen: string | null;
  paymentMethods: tPaymentMethod[];
  hostedPaymentUrl: string | null;
  decline: tPastDueDecline | null;
  preferredPaymentMethodId: string | null;
}): string | null {
  const { chosen, paymentMethods, hostedPaymentUrl } = options;
  if (chosen === PAST_DUE_HOSTED_OPTION && hostedPaymentUrl) return chosen;
  if (
    chosen !== null &&
    paymentMethods.some(
      (pm) => pm.paymentMethodId === chosen && !isExpiredCard(pm),
    )
  ) {
    return chosen;
  }
  return resolveInitialPastDueSelection({
    paymentMethods,
    decline: options.decline,
    preferredPaymentMethodId: options.preferredPaymentMethodId,
  });
}

/** KTD-3: the summary, the cards, the defaults and the local state → the zone's view. */
export function buildPastDuePaymentView(
  options: tBuildPastDuePaymentViewOptions,
): tPastDuePaymentView {
  const { summary, local, paymentMethods, paymentMethodDefaults } = options;
  const currency = displayCurrency(summary);
  const summaryPhase = resolveSummaryPhase({
    summary,
    summaryState: options.summaryState,
  });
  const phase: tPastDuePaymentPhase =
    summaryPhase === 'loading' || options.summaryState === 'failed'
      ? summaryPhase
      : local.phase !== 'idle'
        ? local.phase
        : summaryPhase;

  const debtInvoices = mapDebtInvoices({
    debtInvoices: summary.debtInvoices,
    currency,
  });
  // M-16: the hosted page charges ONE invoice. The BFF publishes it only with
  // exactly one; this is the second layer, written as if it could still come.
  const hostedPaymentUrl =
    summary.hostedPaymentUrl && summary.debtInvoices?.length === 1
      ? summary.hostedPaymentUrl
      : null;
  const decline =
    local.decline ??
    mapRenewalDecline({
      lastDecline: summary.lastDecline,
      lastDeclineSource: summary.lastDeclineSource,
      currency: summary.currency,
    });
  const planPaymentMethodId = paymentMethodDefaults.planPaymentMethodId ?? null;
  const pause = summary.planPause;

  return {
    amountOwed: formatAmountOwed({
      amountMinorUnits: summary.amountRemaining,
      currency: summary.currency,
    }),
    debtInvoices,
    nextRetryDate: parseSummaryDate(summary.nextPaymentAttempt),
    paymentMethods,
    selectedPaymentMethodId: resolvePastDueSelection({
      chosen: local.selectedPaymentMethodId,
      paymentMethods,
      hostedPaymentUrl,
      decline,
      preferredPaymentMethodId: planPaymentMethodId,
    }),
    planPaymentMethodId,
    deadlineShownAbove: (options.daysPastDue ?? 0) >= PAST_DUE_FINAL_DAY,
    isPauseFee:
      pause !== null &&
      pause !== undefined &&
      (pause.status === 'ACTIVE' || pause.inPauseWindow === true),
    // KTD-2: manual collection stays inert until RET-2112 wires it.
    isManualCollection: false,
    dueDate: null,
    transferInstructions: null,
    transferReceived: null,
    transferSupported: false,
    hostedPaymentUrl,
    phase,
    decline,
    partial: local.partial,
    onSelectPaymentMethod: options.callbacks.onSelectPaymentMethod,
    onAddPaymentMethod: options.callbacks.onAddPaymentMethod,
    onPay: options.callbacks.onPay,
  };
}

/**
 * KTD-7: the cards to send back if the bank refuses the charge. The ones of
 * the snapshot RET-1974 wrote before a card add left for the bank's page,
 * when THIS page load is that add's return; otherwise the defaults as the
 * zone found them. A snapshot of another add is never read.
 */
export function resolvePastDuePreviousPaymentMethods(options: {
  defaults: tPaymentMethodDefaults;
  returnSetupIntentId: string | null;
  snapshot: tRedirectReturnSnapshot | null;
}): tPreviousPaymentMethods {
  const { defaults, returnSetupIntentId, snapshot } = options;
  if (
    returnSetupIntentId !== null &&
    snapshot !== null &&
    snapshot.setupIntentId === returnSetupIntentId
  ) {
    return {
      plan: snapshot.previousPaymentMethods.plan ?? null,
      account: snapshot.previousPaymentMethods.account ?? null,
    };
  }
  return {
    plan: defaults.planPaymentMethodId ?? null,
    account: defaults.customerDefaultPaymentMethodId ?? null,
  };
}
